// Reviews — moderation queue backed by /admin/reviews + the existing
// approveReview / rejectReview / spamReview mutations from PR 2.8.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  PageHeader,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  approveReview,
  bulkReviewAction,
  fetchAdminReviews,
  rejectReview,
  spamReview,
  type AdminReviewRow,
  type AdminReviewsListParams,
  type ReviewBulkAction,
} from '@/lib/admin-api';
import type { RowKey } from '@drivecars/admin-ui';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminReviewsListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'spam', label: 'Spam' },
];

interface ActionCtx {
  busy: number | null;
  onApprove: (r: AdminReviewRow) => void;
  onReject: (r: AdminReviewRow) => void;
  onSpam: (r: AdminReviewRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminReviewRow>[] => [
  {
    key: 'rating',
    h: 'Rating',
    cell: (r) => (
      <span className="tabular-nums font-semibold">
        {Number.isFinite(r.rating) ? r.rating.toFixed(1) : '—'}
      </span>
    ),
  },
  {
    key: 'customer',
    h: 'Customer',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.customerName}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.customerEmail ?? '—'}
        </div>
      </div>
    ),
  },
  {
    key: 'car',
    h: 'Car',
    cell: (r) => r.carTitle ?? r.carSlug ?? '—',
  },
  {
    key: 'body',
    h: 'Review',
    cell: (r) => (
      <div className="max-w-[420px]">
        {r.title ? <div className="truncate font-medium">{r.title}</div> : null}
        <div className="line-clamp-2 text-[12px] text-[color:var(--bo-mute)]">
          {r.body ?? '—'}
        </div>
      </div>
    ),
  },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busy === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status !== 'approved' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onApprove(r)}>
              Approve
            </RowAction>
          ) : null}
          {r.status !== 'rejected' ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onReject(r)}>
              Reject
            </RowAction>
          ) : null}
          {r.status !== 'spam' ? (
            <RowAction busy={busy} onClick={() => ctx.onSpam(r)}>
              Spam
            </RowAction>
          ) : null}
        </div>
      );
    },
  },
];

export function Reviews() {
  const [filter, setFilter] = useState<FilterKey>('pending');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);
  const [selectedIds, setSelectedIds] = useState<Set<RowKey>>(new Set());
  const [bulkResult, setBulkResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'reviews', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminReviews(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const approveMut = useMutation({ mutationFn: approveReview, onSuccess: invalidate });
  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => rejectReview(id, reason),
    onSuccess: invalidate,
  });
  const spamMut = useMutation({ mutationFn: spamReview, onSuccess: invalidate });

  const bulkMut = useMutation({
    mutationFn: ({
      ids,
      action,
      reason,
    }: {
      ids: number[];
      action: ReviewBulkAction;
      reason?: string;
    }) => bulkReviewAction(ids, action, reason),
    onSuccess: (res) => {
      setBulkResult({ succeeded: res.succeeded.length, failed: res.failed.length });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const busyId =
    (approveMut.isPending && (approveMut.variables as number)) ||
    (rejectMut.isPending && (rejectMut.variables as { id: number }).id) ||
    (spamMut.isPending && (spamMut.variables as number)) ||
    null;

  const cols = buildColumns({
    busy: busyId,
    onApprove: (r) => approveMut.mutate(r.id),
    onReject: (r) => {
      const reason = window.prompt('Rejection reason (optional):') ?? undefined;
      rejectMut.mutate({ id: r.id, reason });
    },
    onSpam: (r) => spamMut.mutate(r.id),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError =
    approveMut.error || rejectMut.error || spamMut.error || bulkMut.error;

  const runBulk = (action: ReviewBulkAction) => {
    const ids = Array.from(selectedIds, Number).filter(Number.isFinite);
    if (ids.length === 0) return;
    const verb =
      action === 'approve'
        ? 'Approve'
        : action === 'reject'
          ? 'Reject'
          : action === 'spam'
            ? 'Mark as spam'
            : 'Trash';
    if (!window.confirm(`${verb} ${ids.length} review${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }
    let reason: string | undefined;
    if (action === 'reject') {
      reason = window.prompt('Reason for rejection (optional):') ?? undefined;
    }
    bulkMut.mutate({ ids, action, reason });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Trust"
        title="Reviews"
        sub={`${total.toLocaleString()} reviews${filter === 'all' ? '' : ` · ${filter}`}.`}
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load reviews: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      {bulkResult ? (
        <div
          role="status"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[12.5px] ${
            bulkResult.failed === 0
              ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
              : 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
          }`}
        >
          <span>
            Bulk action complete: {bulkResult.succeeded} succeeded
            {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}.
          </span>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search title or body…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      {selectedIds.size > 0 ? (
        <div className="sticky top-[60px] z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 shadow-sm">
          <div className="text-[12.5px]">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ms-3 text-[11.5px] text-[color:var(--bo-mute)] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('approve')}
            >
              Approve all
            </RowAction>
            <RowAction
              busy={bulkMut.isPending}
              onClick={() => runBulk('reject')}
            >
              Reject all
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('spam')}
            >
              Spam all
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('trash')}
            >
              Trash all
            </RowAction>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching reviews.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
        selection={{
          selectedIds,
          onToggle: (id) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          },
          onToggleAll: (ids) => {
            setSelectedIds((prev) => {
              const allHere = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              if (allHere) {
                for (const id of ids) next.delete(id);
              } else {
                for (const id of ids) next.add(id);
              }
              return next;
            });
          },
        }}
      />
    </div>
  );
}
